import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * A small "?" icon that reveals a tooltip with a short explanation of a
 * non-obvious calculation. Use near totals that stem from a multiplier or
 * composite formula (e.g. bathroom tiling qty = size × 2.5).
 *
 * Props:
 *  - title:    Short headline shown in the tooltip (optional).
 *  - formula:  One-line human-readable formula, e.g. "62.5 × $180 = $11,250".
 *  - lines:    Optional array of explanation lines.
 *  - testId:   data-testid forwarded to the trigger button.
 *  - className Extra classes to control positioning.
 */
export default function CalcHelp({ title, formula, lines, testId, className = "" }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId || "calc-help-btn"}
          aria-label="Calculation breakdown"
          className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[#A8A29E] hover:text-[#1C1C1C] transition-colors ${className}`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="end"
        className="max-w-[280px] bg-[#1C1C1C] text-white text-[12px] leading-relaxed px-3 py-2 rounded-md shadow-lg"
      >
        {title && <div className="font-medium mb-1">{title}</div>}
        {formula && (
          <div className="fc-mono text-[11px] text-white/80 mb-1">{formula}</div>
        )}
        {Array.isArray(lines) &&
          lines.filter(Boolean).map((l, i) => (
            <div key={typeof l === "string" ? l : `line-${i}`} className="text-white/90">{l}</div>
          ))}
      </TooltipContent>
    </Tooltip>
  );
}
